import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Building2, Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import { BASE_URL } from '../config';

export default function CreateDepartment() {
  const [departments, setDepartments] = useState([]);
  const [newDeptName, setNewDeptName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${BASE_URL}/api/departments/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDepartments(res.data);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newDeptName.trim()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${BASE_URL}/api/departments/`,
        { dept_name: newDeptName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewDeptName('');
      fetchDepartments();
      // alert('Department created successfully!');
    } catch (err) {
      console.error('Error creating department:', err);
      alert('Error creating department');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (dept) => {
    setEditingId(dept.id);
    setEditingName(dept.dept_name);
  };

  const handleUpdate = async (id) => {
    if (!editingName.trim()) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `${BASE_URL}/api/departments/${id}/`,
        { dept_name: editingName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingId(null);
      setEditingName('');
      fetchDepartments();
      alert('Department updated successfully!');
    } catch (err) {
      console.error('Error updating department:', err);
      alert('Error updating department');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${BASE_URL}/api/departments/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchDepartments();
      alert('Department deleted successfully!');
    } catch (err) {
      console.error('Error deleting department:', err);
      alert('Error deleting department');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-4"
      >
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Department Management</h2>
          <p className="text-slate-600 text-sm">Create and manage departments</p>
        </div>
      </motion.div>

      {/* Create Department Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm border border-slate-200 p-5"
      >
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-medical-600" />
          Create New Department
        </h3>
        <form onSubmit={handleCreate} className="flex gap-3">
          <input
            type="text"
            placeholder="Enter department name"
            value={newDeptName}
            onChange={(e) => setNewDeptName(e.target.value)}
            required
            className="flex-1 max-w-md px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2 bg-medical-500 hover:bg-medical-600 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {loading ? 'Creating...' : 'Create'}
          </button>
        </form>
      </motion.div>

      {/* Departments List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h3 className="text-lg font-semibold text-slate-700 mb-3">All Departments ({departments.length})</h3>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">

        {departments.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {departments.map((dept, index) => (
              <motion.div
                key={dept.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-4 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  {editingId === dept.id ? (
                    <>
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                      />
                      <div className="flex items-center gap-2 ml-3">
                        <button
                          onClick={() => handleUpdate(dept.id)}
                          className="p-2 text-medical-600 hover:bg-medical-50 rounded-lg transition-colors"
                          title="Save"
                        >
                          <Save className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(null);
                            setEditingName('');
                          }}
                          className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Cancel"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-medical-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-medical-600" />
                        </div>
                        <span className="text-lg font-medium text-slate-900">
                          {dept.dept_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(dept)}
                          className="p-2 text-medical-600 hover:bg-medical-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(dept.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Building2 className="w-16 h-16 text-slate-700 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">No departments yet</p>
            <p className="text-slate-600 text-sm mt-2">Create your first department above</p>
          </div>
        )}
        </div>
      </motion.div>
    </div>
  );
}

